import React from 'react';
import {
  User, Music, Headphones, ClipboardList, Wrench,
  MessageCircle, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Design Tokens (match DemoSections / ParkingArrival) ─── */
const PANEL_BG = '#171411';
const SURFACE = '#1e1b16';
const BORDER = '#2a2620';
const MONO = "'DM Mono', monospace";
/* Panel headings render at 18px — under the 1.375rem display floor — so they take
   the mono body face at 500 rather than the display serif. */
const DISPLAY = 'var(--s8-font-mono)';
const BODY = "'Barlow', sans-serif";

const AMBER = '#E6A23C';
const RED = '#EF4444';
const MUTED = '#a39889';
const TEXT = '#F0EBE0';

/* Loose role → icon heuristic for venue-side on-site contacts. */
function iconForRole(role = '') {
  const r = role.toLowerCase();
  if (/foh|sound|audio|monitor/.test(r)) return Headphones;
  if (/stage|manager|production|prod/.test(r)) return ClipboardList;
  if (/tech|crew|host|light/.test(r)) return Wrench;
  return User;
}

function telHref(phone) {
  return `tel:${phone.replace(/\s/g, '')}`;
}

/* Tap a phone: dial on touch devices, copy to clipboard on desktop —
   mirrors the demo ContactTile affordance. */
function tapPhone(phone) {
  if (!phone) return;
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    window.location.href = telHref(phone);
  } else {
    navigator.clipboard.writeText(phone).then(() => toast.success(`Copied ${phone}`));
  }
}

/* ─── Tile ─── */

function Tile({ icon: Icon, name, role, phone, hours, isEmergency, isArtist, whatsappLink, testId }) {
  const accent = isArtist ? AMBER : (isEmergency ? RED : AMBER);
  return (
    <div
      data-testid={testId}
      className="relative flex flex-col items-center gap-1.5 p-3 rounded-xl text-center min-w-0 w-full"
      style={{
        background: SURFACE,
        border: isArtist ? `1px solid ${AMBER}55` : `1px solid ${BORDER}`,
        boxShadow: isArtist ? `inset 0 0 0 1px ${AMBER}18` : 'none',
      }}
    >
      {/* Distinguishing tag: ARTIST (two-sided anchor) or emergency flag */}
      {isArtist ? (
        <span
          data-testid="contact-artist-tag"
          className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full"
          style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 0.6, color: AMBER, background: `${AMBER}1a`, border: `1px solid ${AMBER}40` }}
        >
          ARTIST
        </span>
      ) : isEmergency ? (
        <span
          className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full"
          style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 0.4, color: RED, background: `${RED}14`, border: `1px solid ${RED}40` }}
        >
          <AlertTriangle size={8} /> SOS
        </span>
      ) : null}

      {/* WhatsApp affordance — artist only, server-provided wa.me link (raw phone
          is never exposed for the artist; this is the confirmed-booking channel). */}
      {isArtist && whatsappLink && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-artist-whatsapp"
          className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: '#25D366' }}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageCircle size={12} color="#fff" />
        </a>
      )}

      <div
        className="w-9 h-9 rounded-full flex items-center justify-center mt-1"
        style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}
      >
        <Icon size={16} color={accent} />
      </div>
      <span style={{ fontFamily: BODY, fontSize: 13, fontWeight: 600, color: TEXT }} className="truncate w-full">
        {name}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: 0.3 }} className="truncate w-full">
        {role}
      </span>
      {phone && (
        <button
          onClick={() => tapPhone(phone)}
          data-testid={`${testId}-phone`}
          style={{ fontFamily: MONO, fontSize: 10, color: '#888', background: 'transparent', border: 'none', cursor: 'pointer' }}
          className="truncate w-full hover:opacity-70"
        >
          {phone}
        </button>
      )}
      {hours && (
        <span style={{ fontFamily: MONO, fontSize: 9, color: '#6f675d' }} className="truncate w-full">
          {hours}
        </span>
      )}
    </div>
  );
}

/**
 * KeyContactsPanel — real (non-demo) Showtime "who do I call" panel.
 *
 * Reads the Element 1 contract: booking.dayOf.contacts[] (venue-side on-site
 * contacts, shape {name, role, phone, availableHours, isEmergency}) and surfaces
 * the ARTIST as a first-party contact row from the booking's talent identity.
 * This is the two-sided move: the artist appears as a reachable person in the
 * same list, not a faceless booking.
 *
 * Display only — no editing, no acks, no new API calls. Self-hides when there
 * are no venue contacts AND no resolvable artist.
 */
export function KeyContactsPanel({ contacts, artist }) {
  const venueContacts = (contacts || []).filter((c) => c && c.name);
  const artistName = artist && artist.name ? artist.name : null;

  // Graceful empty — nothing real to show.
  if (venueContacts.length === 0 && !artistName) return null;

  return (
    <div
      data-testid="key-contacts"
      className="mb-6 overflow-hidden"
      style={{ background: PANEL_BG, borderRadius: 16, border: '1px solid rgba(212, 169, 90, 0.12)' }}
    >
      <div className="px-5 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: TEXT }}>
          KEY CONTACTS
        </h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4" style={{ background: SURFACE }}>
        {/* Artist first — the first-party, two-sided anchor. */}
        {artistName && (
          <Tile
            icon={Music}
            name={artistName}
            role={artist.role || 'Artist'}
            isArtist
            whatsappLink={artist.whatsappLink || null}
            testId="contact-tile-artist"
          />
        )}
        {venueContacts.map((c, i) => (
          <Tile
            key={`${c.name}-${i}`}
            icon={iconForRole(c.role)}
            name={c.name}
            role={c.role}
            phone={c.phone || null}
            hours={c.availableHours || null}
            isEmergency={!!c.isEmergency}
            testId={`contact-tile-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default KeyContactsPanel;
